import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Info, BookOpen, Sparkle } from '@phosphor-icons/react';

/**
 * TeenVest logo + hover dropdown that surfaces About Us, Blogs and the
 * Central Message pages. Also exposes a tiny "click me" hint below the logo.
 */
const LogoDropdown = ({ compact = false, showHint = true, homePath = '/dashboard' }) => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const handleEnter = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  const items = [
    { to: '/about',           label: 'About Us',        icon: Info,     testid: 'logo-menu-about' },
    { to: '/blog',            label: 'Blogs',           icon: BookOpen, testid: 'logo-menu-blog' },
    { to: '/central-message', label: 'Central Message', icon: Sparkle,  testid: 'logo-menu-central' },
  ];

  return (
    <div
      className="relative inline-block"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      data-testid="logo-dropdown-wrapper"
    >
      <Link to={homePath} className="flex items-center gap-2 group" data-testid="navbar-logo">
        <div className="relative">
          <img
            src="/logo.png"
            alt="TeenVest — stock market simulation game for teens"
            className={`${compact ? 'w-9 h-9' : 'w-10 h-10'} group-hover:scale-110 transition-transform`}
          />
        </div>
        <div>
          <h1 className={`${compact ? 'text-lg' : 'text-xl'} font-unbounded font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent`}>
            TeenVest
          </h1>
          {showHint && (
            <p className="text-[9px] text-zinc-500 -mt-0.5 italic group-hover:text-gold transition-colors" data-testid="logo-click-hint">
              click me
            </p>
          )}
        </div>
      </Link>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-2 w-56 rounded-xl border border-white/10 bg-zinc-950/95 backdrop-blur-xl shadow-2xl overflow-hidden z-[60]"
            data-testid="logo-dropdown-menu"
          >
            {items.map(({ to, label, icon: Icon, testid }) => (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-sm text-zinc-200 hover:bg-white/5 hover:text-gold transition-colors"
                data-testid={testid}
              >
                <Icon size={18} weight="duotone" className="text-gold" />
                <span className="font-medium">{label}</span>
              </Link>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LogoDropdown;
